import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Fab,
  Snackbar,
  Alert,
  useMediaQuery,
  IconButton,
  Card,
  CardContent,
  Paper,
  Grid,
  Avatar,
  Badge
} from '@mui/material';
import axios from '../utils/axios';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  Delete as DeleteIcon, 
  Download as DownloadIcon, 
  Edit as EditIcon, 
  Save as SaveIcon, 
  Close as CloseIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  People as FamilyIcon,
  School as SchoolIcon,
  AccountBalance as ChurchIcon,
  PhotoCamera as CameraIcon,
  Visibility as VisibilityIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import { useError } from '../contexts/ErrorContext';
import { useLoading } from '../contexts/LoadingContext';
import ReligiousEducationPDF from './ReligiousEducationPDF';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useTheme } from '@mui/material/styles';

// Legacy hardcoded labels for the five original Squarespace program codes. Records
// from prior years use these and must render with their original names even after
// the Program collection has changed.
const LEGACY_PROGRAM_LABELS = {
  'veritas1-5': 'Veritas Grade 1-5',
  'veritas6-8': 'Veritas Grade 6-8',
  'ocic': 'OCIC',
  'wfc': 'Whole Family Catechesis',
  'ocit': 'OCIT'
};

const programSlugOf = (program) => (program?.name || '').toLowerCase().replace(/\s+/g, '-');

// Resolve a stored program slug to a human label. Legacy map -> live program list
// for the record's school year -> title-cased fallback. Never mutates input.
const resolveProgramLabel = (slug, programs) => {
  if (LEGACY_PROGRAM_LABELS[slug]) return LEGACY_PROGRAM_LABELS[slug];
  const live = (programs || []).find(p => programSlugOf(p) === slug);
  if (live) return live.name;
  if (!slug) return '';
  return slug
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function formatPhoneNumber(phoneNumberString) {
  if (!phoneNumberString) return 'N/A';
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  if (cleaned.length === 10) {
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }
  return phoneNumberString; // Return original if not a 10-digit number or already formatted strangely
}

// New function for formatting phone number as user types in input field
function getFormattedPhoneNumberForInput(digits) {
  if (!digits) return '';
  const cleanedDigits = ('' + digits).replace(/\D/g, ''); // Should be digits already, but good practice

  if (cleanedDigits.length === 0) return '';

  let formattedValue = '(';
  // Add first part of area code
  formattedValue += cleanedDigits.substring(0, Math.min(3, cleanedDigits.length));

  // Add closing parenthesis and space if area code part is complete and there are more digits
  if (cleanedDigits.length > 3) {
    formattedValue += ') ' + cleanedDigits.substring(3, Math.min(6, cleanedDigits.length));
  }
  
  // Add dash and last part of number if middle part is complete and there are more digits
  if (cleanedDigits.length > 6) {
    formattedValue += '-' + cleanedDigits.substring(6, Math.min(10, cleanedDigits.length));
  }
  return formattedValue;
}

const SectionCard = ({ title, icon, children, sx = {} }) => (
  <Card 
    elevation={2} 
    sx={{ 
      mb: 3, 
      borderRadius: 3,
      overflow: 'visible',
      border: '1px solid',
      borderColor: 'divider',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        elevation: 4,
        transform: 'translateY(-2px)',
      },
      ...sx 
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const ChildCard = ({ child, index, isEditing, handleChange, handleUploadCertificate, selectedFiles, setSelectedFiles, onDeleteChild, programs = [] }) => {
  const theme = useTheme();
  
  return (
    <Card 
      elevation={1} 
      sx={{ 
        mb: 2, 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%)'
      }}
    >
      <Accordion 
        defaultExpanded={false}
        elevation={0}
        sx={{ 
          background: 'transparent',
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            px: 3, 
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
              {index + 1}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {child.name || `Child ${index + 1}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Grade {child.grade || 'N/A'} • {child.program?.length || 0} program(s)
              </Typography>
            </Box>
          </Box>
          {isEditing && (
            <IconButton 
              aria-label="delete child"
              title="Remove Child"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteChild(index);
              }}
              color="error"
              size="medium"
              sx={{ ml: 1 }}
            >
              <DeleteForeverIcon />
            </IconButton>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 3 }}>
          <Grid container spacing={3}>
            {isEditing ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={child.name || ''}
                    onChange={e => handleChange(`children.${index}.name`, e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Grade"
                    value={child.grade || ''}
                    onChange={e => handleChange(`children.${index}.grade`, e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={child.dateOfBirth ? child.dateOfBirth.split('T')[0] : ''}
                    onChange={e => handleChange(`children.${index}.dateOfBirth`, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={child.catholicSchool?.attends || false}
                          onChange={e => handleChange(`children.${index}.catholicSchool.attends`, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Attends Catholic School</Typography>}
                    />
                    {child.catholicSchool?.attends && (
                      <TextField
                        fullWidth
                        label="School Name"
                        value={child.catholicSchool.schoolName || ''}
                        onChange={e => handleChange(`children.${index}.catholicSchool.schoolName`, e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                    Sacraments Received
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {[{ display: 'Baptism', value: 'baptism' }, { display: 'First Communion', value: 'communion' }, { display: 'Confirmation', value: 'confirmation' }].map(sacramentMap => (
                      <Chip
                        key={sacramentMap.value}
                        label={sacramentMap.display}
                        color={child.sacraments?.received?.includes(sacramentMap.value) ? 'primary' : 'default'}
                        onClick={() => {
                          const received = child.sacraments?.received?.includes(sacramentMap.value)
                            ? child.sacraments.received.filter(s => s !== sacramentMap.value)
                            : [...(child.sacraments?.received || []), sacramentMap.value];
                          handleChange(`children.${index}.sacraments.received`, received);
                        }}
                        variant={child.sacraments?.received?.includes(sacramentMap.value) ? 'filled' : 'outlined'}
                        clickable
                        sx={{ 
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': { transform: 'translateY(-1px)' }
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'secondary.main' }}>
                    Preparing For
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {[{ display: 'Baptism', value: 'baptism' }, { display: 'First Communion', value: 'communion' }, { display: 'Confirmation', value: 'confirmation' }].map(sacramentMap => (
                      <Chip
                        key={sacramentMap.value}
                        label={sacramentMap.display}
                        color={child.sacraments?.preparing?.includes(sacramentMap.value) ? 'secondary' : 'default'}
                        onClick={() => {
                          const preparing = child.sacraments?.preparing?.includes(sacramentMap.value)
                            ? child.sacraments.preparing.filter(s => s !== sacramentMap.value)
                            : [...(child.sacraments?.preparing || []), sacramentMap.value];
                          handleChange(`children.${index}.sacraments.preparing`, preparing);
                        }}
                        variant={child.sacraments?.preparing?.includes(sacramentMap.value) ? 'filled' : 'outlined'}
                        clickable
                        sx={{ 
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': { transform: 'translateY(-1px)' }
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'success.main' }}>
                    Programs
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {(() => {
                      // Render a chip for every slug already on this child PLUS every
                      // live program from the record's school year. Dedupe by slug so
                      // legacy values like 'wfc' aren't silently dropped from the editor.
                      const liveSlugs = programs.map(programSlugOf);
                      const childSlugs = child.program || [];
                      const allSlugs = Array.from(new Set([...childSlugs, ...liveSlugs]));
                      return allSlugs.map(slug => {
                        const selected = childSlugs.includes(slug);
                        const isLegacyOnly = !liveSlugs.includes(slug);
                        return (
                          <Chip
                            key={slug}
                            label={resolveProgramLabel(slug, programs)}
                            color={selected ? 'success' : 'default'}
                            onClick={() => {
                              const next = selected
                                ? childSlugs.filter(p => p !== slug)
                                : [...childSlugs, slug];
                              handleChange(`children.${index}.program`, next);
                            }}
                            variant={selected ? 'filled' : 'outlined'}
                            clickable
                            title={isLegacyOnly ? 'Legacy program preserved from a prior year' : undefined}
                            sx={{
                              borderRadius: 2,
                              transition: 'all 0.2s ease-in-out',
                              fontStyle: isLegacyOnly ? 'italic' : 'normal',
                              '&:hover': { transform: 'translateY(-1px)' }
                            }}
                          />
                        );
                      });
                    })()}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={child.baptism?.baptizedAtStBridget || false}
                              onChange={e => handleChange(`children.${index}.baptism.baptizedAtStBridget`, e.target.checked)}
                              color="info"
                            />
                          }
                          label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Baptized at St. Bridget</Typography>}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={child.baptism?.previouslyEnrolled || false}
                              onChange={e => handleChange(`children.${index}.baptism.previouslyEnrolled`, e.target.checked)}
                              color="info"
                            />
                          }
                          label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Previously Enrolled</Typography>}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChurchIcon color={child.baptism?.baptizedAtStBridget ? 'primary' : 'error'} fontSize="small" />
                      <Typography variant="body2" color={child.baptism?.baptizedAtStBridget ? 'text.primary' : 'error.main'}>
                        {child.baptism?.baptizedAtStBridget ? 'Baptized at St. Bridget' : 'Not Baptized at St. Bridget'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon color={child.baptism?.previouslyEnrolled ? 'primary' : 'error'} fontSize="small" />
                      <Typography variant="body2" color={child.baptism?.previouslyEnrolled ? 'text.primary' : 'error.main'}>
                        {child.baptism?.previouslyEnrolled ? 'Previously Enrolled' : 'Not Previously Enrolled'}
                      </Typography>
                    </Box>
                    {child.baptism?.certificatePath && (
                      child.baptism.certificatePath.startsWith('gdrive:') ? (
                        (() => {
                          const parts = child.baptism.certificatePath.split(':');
                          const webViewLink = parts.length > 2 ? parts.slice(2).join(':') : '';
                          return webViewLink && webViewLink.startsWith('http') ? (
                            <Button
                              href={webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              sx={{ textTransform: 'none', fontWeight: 500 }}
                            >
                              View Baptism Certificate
                            </Button>
                          ) : (
                            <span style={{ color: 'red', fontSize: 14 }}>Invalid certificate link</span>
                          );
                        })()
                      ) : (
                        <Button
                          href={`/${child.baptism.certificatePath.replace(/^uploads[\/]/, 'uploads/')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                          View Baptism Certificate
                        </Button>
                      )
                    )}
                  </Box>
                </Grid>
                {isEditing && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 180, fontWeight: 500 }}>
                        Upload Baptism Certificate
                      </Typography>
                      <input
                        id={`certificate-upload-${index}`}
                        type="file"
                        accept=".pdf,image/*"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files[0];
                          if (file && handleUploadCertificate) handleUploadCertificate(index, file);
                          // Show file name in UI (set state)
                          if (file) setSelectedFiles(prev => ({ ...prev, [index]: file.name }));
                        }}
                      />
                      <label htmlFor={`certificate-upload-${index}`} style={{ margin: 0 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          component="span"
                          startIcon={<CameraIcon />}
                          sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                          Choose File
                        </Button>
                      </label>
                      <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                        {selectedFiles?.[index] || 'No file selected'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                        (PDF, JPG, PNG, max 5MB)
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      Personal Information
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Name:</strong> {child.name || 'N/A'}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong>Date of Birth:</strong> {child.dateOfBirth ? new Date(child.dateOfBirth).toLocaleDateString() : 'N/A'}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Grade:</strong> {child.grade || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      School Information
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body1">
                        <strong>Catholic School:</strong> {child.catholicSchool?.attends ? `Yes (${child.catholicSchool.schoolName || 'N/A'})` : 'No'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                    Sacraments Received
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {child.sacraments?.received?.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                        {child.sacraments.received.map(s => (
                          <Chip 
                            key={s} 
                            label={({ baptism: 'Baptism', communion: 'First Communion', confirmation: 'Confirmation' })[s] || s} 
                            size="small" 
                            color="primary" 
                            variant="filled"
                            sx={{ borderRadius: 2 }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">None</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                    Preparing For
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {child.sacraments?.preparing?.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                        {child.sacraments.preparing.map(s => (
                          <Chip 
                            key={s} 
                            label={({ baptism: 'Baptism', communion: 'First Communion', confirmation: 'Confirmation' })[s] || s} 
                            size="small" 
                            color="secondary" 
                            variant="filled"
                            sx={{ borderRadius: 2 }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">None</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                    Programs
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {child.program?.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                        {child.program.map(p => (
                          <Chip
                            key={p}
                            label={resolveProgramLabel(p, programs)}
                            size="small"
                            color="success"
                            variant="filled"
                            sx={{ borderRadius: 2 }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">None</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChurchIcon color={child.baptism?.baptizedAtStBridget ? 'primary' : 'error'} fontSize="small" />
                      <Typography variant="body2" color={child.baptism?.baptizedAtStBridget ? 'text.primary' : 'error.main'}>
                        {child.baptism?.baptizedAtStBridget ? 'Baptized at St. Bridget' : 'Not Baptized at St. Bridget'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon color={child.baptism?.previouslyEnrolled ? 'primary' : 'error'} fontSize="small" />
                      <Typography variant="body2" color={child.baptism?.previouslyEnrolled ? 'text.primary' : 'error.main'}>
                        {child.baptism?.previouslyEnrolled ? 'Previously Enrolled' : 'Not Previously Enrolled'}
                      </Typography>
                    </Box>
                    {child.baptism?.certificatePath && (
                      child.baptism.certificatePath.startsWith('gdrive:') ? (
                        (() => {
                          const parts = child.baptism.certificatePath.split(':');
                          const webViewLink = parts.length > 2 ? parts.slice(2).join(':') : '';
                          return webViewLink && webViewLink.startsWith('http') ? (
                            <Button
                              href={webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              sx={{ textTransform: 'none', fontWeight: 500 }}
                            >
                              View Baptism Certificate
                            </Button>
                          ) : (
                            <span style={{ color: 'red', fontSize: 14 }}>Invalid certificate link</span>
                          );
                        })()
                      ) : (
                        <Button
                          href={`/${child.baptism.certificatePath.replace(/^uploads[\/]/, 'uploads/')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                          View Baptism Certificate
                        </Button>
                      )
                    )}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

const PaymentHistory = ({ transactions }) => (
  <SectionCard title="Payment History" icon={<PaymentIcon />}>
    {transactions?.length > 0 ? (
      <List sx={{ p: 0 }}>
        {transactions.map((transaction, idx) => (
          <ListItem 
            key={transaction._id || idx}
            sx={{ 
              px: 0, 
              py: 1,
              borderBottom: idx < transactions.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider'
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ${transaction.amount?.toFixed(2)}
                  </Typography>
                  <Chip 
                    label={transaction.method ? transaction.method.charAt(0).toUpperCase() + transaction.method.slice(1) : ''} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              }
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'}
                  {transaction.reference ? ` • ${transaction.reference}` : ''}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    ) : (
      <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No payment transactions recorded.
      </Typography>
    )}
  </SectionCard>
);

const ReligiousEducationDetails = ({ registration, open, onClose, onUpdate }) => {
  const [editedRegistration, setEditedRegistration] = useState(deepClone(registration));
  const [isEditing, setIsEditing] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newPayment, setNewPayment] = useState({ amount: 0, method: 'cash', reference: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [settings, setSettings] = useState(null);
  // Live programs scoped to THIS registration's school year — for label resolution
  // and as the editable chip set. Old records (with legacy short codes like 'wfc') are
  // preserved either way because the resolver and chip union both keep stored values.
  const [programs, setPrograms] = useState([]);
  const { showError } = useError();
  const { showLoading, hideLoading } = useLoading();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});

  useEffect(() => {
    setEditedRegistration(deepClone(registration));
    if (open) setIsEditing(false);
  }, [registration, open]);

  // Fetch settings for fee calculation
  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  // Fetch the program list for THIS registration's school year (preserves correct
  // labels when viewing past-year records). Falls back to current programs when the
  // registration has no schoolYear stored (very old data).
  useEffect(() => {
    if (!open) return;
    const recordYear = registration?.schoolYear;
    const fetchPrograms = async () => {
      try {
        if (recordYear) {
          const response = await axios.get('/programs', {
            params: { schoolYear: recordYear, active: 'true' }
          });
          setPrograms(Array.isArray(response.data) ? response.data : []);
        } else {
          const response = await axios.get('/programs/current');
          setPrograms(response.data?.programs || []);
        }
      } catch (error) {
        console.error('Failed to fetch programs for label resolution:', error);
        setPrograms([]);
      }
    };
    fetchPrograms();
  }, [open, registration?.schoolYear]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/religious-education/settings/status-public');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  // Calculate detailed fee breakdown for receipt
  const calculateDetailedBreakdown = () => {
    if (!settings || !editedRegistration) return { items: [], total: 0 };

    const items = [];
    let total = 0;
    
    // Try to determine if discount was active at registration time
    const registrationDate = editedRegistration.registrationDate ? new Date(editedRegistration.registrationDate) : new Date();
    const isDiscountActive = settings.discount?.enabled && 
                             settings.discount?.endDate && 
                             registrationDate < new Date(settings.discount.endDate);

    editedRegistration.children?.forEach((child, index) => {
      const isCatholicSchoolStudent = child.catholicSchool?.attends;
      const childName = child.name || `Child ${index + 1}`;
      
      // Registration fee (free for Catholic school students)
      if (!isCatholicSchoolStudent) {
        if (index === 0) {
          // First child registration
          const fee = isDiscountActive ? 
            (settings.discount.discountedRegistrationFee || 0) : 
            (settings.registrationFee || 0);
          
          if (fee > 0) {
            items.push({
              type: 'registration',
              description: `Registration Fee - ${childName} (1st child)`,
              originalPrice: settings.registrationFee || 0,
              discountedPrice: isDiscountActive ? (settings.discount.discountedRegistrationFee || 0) : null,
              amount: fee,
              isDiscount: isDiscountActive
            });
            total += fee;
          }
        } else {
          // Additional child registration
          const fee = isDiscountActive ? 
            (settings.discount.discountedAdditionalChildFee || 0) : 
            (settings.additionalChildFee || 0);
          
          if (fee > 0) {
            items.push({
              type: 'registration',
              description: `Registration Fee - ${childName} (additional child)`,
              originalPrice: settings.additionalChildFee || 0,
              discountedPrice: isDiscountActive ? (settings.discount.discountedAdditionalChildFee || 0) : null,
              amount: fee,
              isDiscount: isDiscountActive
            });
            total += fee;
          }
        }
      } else {
        // Catholic school student - free registration
        items.push({
          type: 'registration',
          description: `Registration Fee - ${childName} (Catholic School Student)`,
          originalPrice: index === 0 ? (settings.registrationFee || 0) : (settings.additionalChildFee || 0),
          amount: 0,
          isFree: true
        });
      }

      // Sacramental preparation fees
      if (child.sacraments?.preparing && child.sacraments.preparing.length > 0) {
        const sacramentFee = settings.sacramentalPreparationFee || 0;
        const sacramentsText = child.sacraments.preparing.map(s => 
          s.charAt(0).toUpperCase() + s.slice(1)
        ).join(', ');
        
        items.push({
          type: 'sacrament',
          description: `Sacramental Preparation - ${childName} (${sacramentsText})`,
          amount: sacramentFee,
          originalPrice: sacramentFee
        });
        total += sacramentFee;
      }
    });

    return { items, total };
  };

  const handleChange = (path, value) => {
    setEditedRegistration(prev => {
      const newData = deepClone(prev);
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleEditClick = () => setIsEditing(true);
  const handleCancelEdit = () => {
    setEditedRegistration(deepClone(registration));
    setIsEditing(false);
  };

  const handleDeleteChild = (childIndex) => {
    if (window.confirm(`Are you sure you want to remove ${editedRegistration.children[childIndex]?.name || `Child ${childIndex + 1}`}? This action will be permanent upon saving.`)) {
      const currentChildren = editedRegistration.children || [];
      const updatedChildren = currentChildren.filter((_, idx) => idx !== childIndex);
      handleChange('children', updatedChildren);
      // Also clear any selected file for the deleted child index if it was the last one
      // or shift subsequent selected files if necessary (though simpler to just clear all or specific for now)
      setSelectedFiles(prev => {
        const newSelectedFiles = {...prev};
        delete newSelectedFiles[childIndex];
        // If you need to re-index other files, that logic would go here.
        // For now, just removing the specific index is fine.
        return newSelectedFiles;
      });
    }
  };

  const handleSave = async () => {
    try {
      if (showLoading && typeof showLoading === 'function') {
        showLoading();
      }

      // Only send editable fields, including notes, but not activity
      const {
        _id,
        family,
        children,
        payment,
        permissions,
        signature,
        status,
        notes // include notes since they are editable
      } = editedRegistration;

      const dataToSend = {
        _id,
        family,
        children,
        payment,
        permissions,
        signature,
        status,
        notes // include notes
      };

      console.log('Saving registration data (dataToSend):', dataToSend);
      console.log('Size of dataToSend (JSON stringified):', JSON.stringify(dataToSend).length, 'bytes');

      const response = await axios.put(`/religious-education/${registration._id}`, dataToSend);
      
      // Update local state with the fresh data from server
      if (response.data) {
        setEditedRegistration(deepClone(response.data));
      }
      
      setSnackbar({ open: true, message: 'Registration updated successfully!', severity: 'success' });
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate();
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving registration:', error);
      setSnackbar({ open: true, message: 'Failed to update registration', severity: 'error' });
      if (showError && typeof showError === 'function') {
        showError('Failed to update religious education registration');
      }
    } finally {
      if (hideLoading && typeof hideLoading === 'function') {
        hideLoading();
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      try {
        if (showLoading && typeof showLoading === 'function') {
          showLoading();
        }
        await axios.delete(`/religious-education/${registration._id}`);
        setSnackbar({ open: true, message: 'Registration deleted successfully!', severity: 'success' });
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate();
        }
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
      } catch (error) {
        console.error('Error deleting registration:', error);
        setSnackbar({ open: true, message: 'Failed to delete registration', severity: 'error' });
        if (showError && typeof showError === 'function') {
          showError('Failed to delete religious education registration');
        }
      } finally {
        if (hideLoading && typeof hideLoading === 'function') {
          hideLoading();
        }
      }
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      if (showLoading && typeof showLoading === 'function') {
        showLoading();
      }
      await axios.post(`/religious-education/${editedRegistration._id}/notes`, { content: newNote });
      setNewNote('');
      setSnackbar({ open: true, message: 'Note added successfully!', severity: 'success' });
      // Immediately fetch the latest registration data so the new note is visible
      const response = await axios.get(`/religious-education/${editedRegistration._id}`);
      if (response.data) {
        setEditedRegistration(deepClone(response.data));
      }
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setSnackbar({ open: true, message: 'Failed to add note', severity: 'error' });
      if (showError && typeof showError === 'function') {
        showError('Failed to add note');
      }
    } finally {
      if (hideLoading && typeof hideLoading === 'function') {
        hideLoading();
      }
    }
  };

  const handleAddPayment = async () => {
    if (newPayment.amount <= 0) return;
    try {
      if (showLoading && typeof showLoading === 'function') {
        showLoading();
      }
      await axios.post(`/religious-education/${editedRegistration._id}/payment/transaction`, newPayment);
      setNewPayment({ amount: 0, method: 'cash', reference: '' });
      setSnackbar({ open: true, message: 'Payment added successfully!', severity: 'success' });
      // Immediately fetch the latest registration data so the new payment is visible
      const response = await axios.get(`/religious-education/${editedRegistration._id}`);
      if (response.data) {
        setEditedRegistration(deepClone(response.data));
      }
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate();
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      setSnackbar({ open: true, message: 'Failed to add payment', severity: 'error' });
      if (showError && typeof showError === 'function') {
        showError('Failed to add payment');
      }
    } finally {
      if (hideLoading && typeof hideLoading === 'function') {
        hideLoading();
      }
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'open': return 'warning';
      case 'hardship': return 'info';
      default: return 'default';
    }
  };

  useEffect(() => {
    if (!isEditing && editedRegistration && editedRegistration.payment) {
      const totalPaid = editedRegistration.payment.transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
      const balanceDue = (editedRegistration.payment.amount || 0) - totalPaid;
      if (editedRegistration.payment.status !== 'hardship') {
        if (balanceDue > 0 && editedRegistration.payment.status !== 'open') {
          setEditedRegistration(prev => ({
            ...prev,
            payment: {
              ...prev.payment,
              status: 'open'
            }
          }));
        } else if (balanceDue === 0 && editedRegistration.payment.status !== 'paid') {
          setEditedRegistration(prev => ({
            ...prev,
            payment: {
              ...prev.payment,
              status: 'paid'
            }
          }));
        }
      }
    }
  }, [editedRegistration.payment?.transactions, editedRegistration.payment?.amount, isEditing]);

  // In the Notes SectionCard, sort notes by most recent first
  const sortedNotes = useMemo(() =>
    (editedRegistration.notes || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date)),
    [editedRegistration.notes]
  );

  // Sort activity log by most recent first
  const sortedActivity = useMemo(() => 
    (editedRegistration.activity || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date)),
    [editedRegistration.activity]
  );

  const handleUploadCertificate = async (childIndex, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('certificate', file);
    try {
      if (showLoading && typeof showLoading === 'function') showLoading();
      await axios.post(`/religious-education/${editedRegistration._id}/certificate/${childIndex}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Refresh registration data
      const response = await axios.get(`/religious-education/${editedRegistration._id}`);
      if (response.data) setEditedRegistration(deepClone(response.data));
    } catch (err) {
      if (showError && typeof showError === 'function') showError('Failed to upload certificate');
    } finally {
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          bgcolor: 'grey.50'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Religious Education Registration
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {editedRegistration.family?.name || 'Family Details'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ alignItems: 'center' }}>
          {!isEditing && (
            <Button 
              startIcon={<EditIcon />} 
              onClick={handleEditClick} 
              variant="outlined"
              sx={{
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                minWidth: '100px',
                '&:hover': { 
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Edit
            </Button>
          )}
          <PDFDownloadLink
            document={<ReligiousEducationPDF registration={registration} programs={programs} />}
            fileName={`${registration.family.name.replace(/\s+/g, '_')}_Religious_Education_Registration-${new Date().toISOString().split('T')[0]}.pdf`}
            style={{ textDecoration: 'none' }}
          >
            {({ loading }) => (
              <Button 
                startIcon={<DownloadIcon />} 
                disabled={loading} 
                variant="outlined"
                sx={{
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.5)',
                  minWidth: '100px',
                  '&:hover': { 
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                {loading ? 'Generating...' : 'Download PDF'}
              </Button>
            )}
          </PDFDownloadLink>
          <Button 
            startIcon={<DeleteIcon />} 
            onClick={handleDelete} 
            variant="outlined"
            sx={{ 
              color: '#ffcdd2',
              borderColor: 'rgba(255, 121, 121, 0.5)',
              minWidth: '100px',
              '&:hover': { 
                borderColor: '#ef5350',
                bgcolor: 'rgba(239, 83, 80, 0.1)',
                color: '#ef5350'
              }
            }}
          >
            Delete
          </Button>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
        <Box sx={{ p: 3 }}>
          {/* Family Information */}
          <SectionCard title="Family Information" icon={<FamilyIcon />}>
            {isEditing ? (
              <Grid container spacing={3}>
                {/* Parents - Display only, editing parents requires more complex UI */}
                {editedRegistration.family?.parents && editedRegistration.family.parents.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Parents/Guardians</Typography>
                    {editedRegistration.family.parents.map((parent, idx) => (
                      <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>{parent.relationship}:</strong> {parent.name}
                          {parent.maidenName && ` (maiden: ${parent.maidenName})`}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Family Name"
                    value={editedRegistration.family?.name || ''}
                    onChange={e => handleChange('family.name', e.target.value)}
                    variant="outlined"
                    helperText="Auto-generated from parent names"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Primary Contact Name"
                    value={editedRegistration.family?.primaryContact?.name || ''}
                    onChange={e => handleChange('family.primaryContact.name', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={editedRegistration.family?.primaryContact?.email || ''}
                    onChange={e => handleChange('family.primaryContact.email', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={getFormattedPhoneNumberForInput(editedRegistration.family?.primaryContact?.phone || '')}
                    onChange={e => {
                      const rawValue = e.target.value;
                      let currentDigits = ('' + rawValue).replace(/\D/g, '');
                      if (currentDigits.length > 10) {
                        currentDigits = currentDigits.substring(0, 10);
                      }
                      handleChange('family.primaryContact.phone', currentDigits);
                    }}
                    variant="outlined"
                    inputProps={{ maxLength: 14 }} // Max length for (XXX) XXX-XXXX
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {/* Parents/Guardians Section */}
                {editedRegistration.family?.parents && editedRegistration.family.parents.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Parents/Guardians</Typography>
                    {editedRegistration.family.parents.map((parent, idx) => (
                      <Typography key={idx} variant="body1" sx={{ fontWeight: 500 }}>
                        {parent.relationship}: {parent.name}
                        {parent.maidenName && <Typography component="span" color="text.secondary"> (maiden: {parent.maidenName})</Typography>}
                      </Typography>
                    ))}
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Family Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{editedRegistration.family?.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Primary Contact</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{editedRegistration.family?.primaryContact?.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Email Address</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{editedRegistration.family?.primaryContact?.email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Phone Number</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatPhoneNumber(editedRegistration.family?.primaryContact?.phone) || 'N/A'}</Typography>
                </Grid>
              </Grid>
            )}
          </SectionCard>

          {/* Children */}
          <SectionCard title="Children Enrolled" icon={<SchoolIcon />}>
            {editedRegistration.children?.map((child, idx) => (
              <ChildCard
                key={child._id || idx}
                child={child}
                index={idx}
                isEditing={isEditing}
                handleChange={handleChange}
                handleUploadCertificate={handleUploadCertificate}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                onDeleteChild={handleDeleteChild}
                programs={programs}
              />
            ))}
          </SectionCard>

          {/* Permissions */}
          <SectionCard title="Permissions" icon={<CameraIcon />}>
            <Paper sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editedRegistration.permissions?.photoPermission || false}
                    onChange={e => handleChange('permissions.photoPermission', e.target.checked)}
                    disabled={!isEditing}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Permission to photograph child for parish publications and website
                  </Typography>
                }
              />
            </Paper>
          </SectionCard>

          {/* Payment Information */}
          <SectionCard title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Payment Information
              </Typography>
              <Chip
                label={editedRegistration.payment?.status ? editedRegistration.payment.status.charAt(0).toUpperCase() + editedRegistration.payment.status.slice(1) : 'Open'}
                color={
                  editedRegistration.payment?.status === 'paid' ? 'success' :
                  editedRegistration.payment?.status === 'hardship' ? 'info' :
                  'warning'
                }
                variant="filled"
                sx={{ borderRadius: 2, fontWeight: 600 }}
              />
              {isEditing && (
                editedRegistration.payment?.status !== 'hardship' ? (
                  <Button
                    variant="outlined"
                    color="info"
                    sx={{ ml: 2 }}
                    onClick={() => handleChange('payment.status', 'hardship')}
                  >
                    Mark as Hardship
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ ml: 2 }}
                    onClick={() => {
                      const totalPaid = editedRegistration.payment.transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
                      const balanceDue = (editedRegistration.payment.amount || 0) - totalPaid;
                      handleChange('payment.status', totalPaid >= (editedRegistration.payment.amount || 0) ? 'paid' : 'open');
                    }}
                  >
                    Remove Hardship
                  </Button>
                )
              )}
            </Box>
          } icon={<PaymentIcon />}>
            {/* Payment Summary */}
            <Paper sx={{ p: 3, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200', mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'info.main' }}>
                Payment Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      Original Total
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      ${editedRegistration.payment?.amount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      Total Paid
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                      ${editedRegistration.payment?.transactions?.reduce((sum, t) => sum + (t.amount || 0), 0)?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                      Balance Due
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: (editedRegistration.payment?.amount || 0) - (editedRegistration.payment?.transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0) > 0 ? 'error.main' : 'success.main' }}>
                      ${Math.max(0, (editedRegistration.payment?.amount || 0) - (editedRegistration.payment?.transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0)).toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Detailed Fee Breakdown Receipt */}
            {settings && (
              <Paper sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                  📄 Registration Fee Breakdown
                  {(() => {
                    const registrationDate = editedRegistration.registrationDate ? new Date(editedRegistration.registrationDate) : new Date();
                    const isDiscountActive = settings.discount?.enabled && 
                                             settings.discount?.endDate && 
                                             registrationDate < new Date(settings.discount.endDate);
                    return isDiscountActive && (
                      <Chip 
                        label={`Early Registration Discount Applied`}
                        color="success" 
                        size="small" 
                        sx={{ ml: 2 }} 
                      />
                    );
                  })()}
                </Typography>
                
                {(() => {
                  const breakdown = calculateDetailedBreakdown();
                  
                  if (breakdown.items.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Fee breakdown not available - registration may be from before detailed tracking was implemented.
                      </Typography>
                    );
                  }

                  return (
                    <Box>
                      {/* Line Items */}
                      {breakdown.items.map((item, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            py: 1.5,
                            borderBottom: index < breakdown.items.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.description}
                            </Typography>
                            {item.isDiscount && (
                              <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                Early Registration Discount Applied
                              </Typography>
                            )}
                            {item.isFree && (
                              <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                                Free for Catholic School Students
                              </Typography>
                            )}
                          </Box>
                          
                          <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                            {item.isDiscount && item.discountedPrice !== null ? (
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}
                                >
                                  ${item.originalPrice.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                  ${item.amount.toFixed(2)}
                                </Typography>
                              </Box>
                            ) : item.isFree ? (
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}
                                >
                                  ${item.originalPrice.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color="info.main" sx={{ fontWeight: 600 }}>
                                  FREE
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                ${item.amount.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                      
                      {/* Total Line */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          pt: 2,
                          mt: 2,
                          borderTop: '2px solid',
                          borderColor: 'primary.main'
                        }}
                      >
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                          Total Registration Fee:
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                          ${breakdown.total.toFixed(2)}
                        </Typography>
                      </Box>

                      {/* Helper Text */}
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          <strong>For Family Reference:</strong> This breakdown shows the detailed fee calculation for this registration. 
                          Staff can use this to explain charges and answer payment questions.
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Paper>
            )}

            {/* Payment History */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
              Payment History
            </Typography>
            {editedRegistration.payment?.transactions?.length > 0 ? (
              <List sx={{ p: 0 }}>
                {editedRegistration.payment.transactions.map((transaction, idx) => (
                  <ListItem
                    key={transaction._id || idx}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: idx < editedRegistration.payment.transactions.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            ${transaction.amount?.toFixed(2)}
                          </Typography>
                          <Chip 
                            label={transaction.method ? transaction.method.charAt(0).toUpperCase() + transaction.method.slice(1) : ''} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'}
                          {transaction.reference ? ` • ${transaction.reference}` : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No payment transactions recorded.
              </Typography>
            )}

            {/* Add Payment Button or Form (not in edit mode) */}
            {!isEditing && (
              <Box sx={{ mt: 2 }}>
                {!showAddPayment ? (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => setShowAddPayment(true)}
                  >
                    Add Payment
                  </Button>
                ) : (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      mt: 1,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      maxWidth: 600,
                      border: '1px solid',
                      borderColor: 'success.light',
                      bgcolor: 'success.50'
                    }}
                  >
                    <TextField
                      label="Amount"
                      type="number"
                      value={newPayment.amount}
                      onChange={e => setNewPayment({ ...newPayment, amount: parseFloat(e.target.value) })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Method"
                      select
                      value={newPayment.method}
                      onChange={e => setNewPayment({ ...newPayment, method: e.target.value })}
                      size="small"
                      SelectProps={{ native: true }}
                      sx={{ width: 120 }}
                    >
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="card">Card</option>
                      <option value="other">Other</option>
                    </TextField>
                    <TextField
                      label="Reference"
                      value={newPayment.reference}
                      onChange={e => setNewPayment({ ...newPayment, reference: e.target.value })}
                      size="small"
                      sx={{ width: 160 }}
                    />
                    <Button
                      variant="contained"
                      color="success"
                      onClick={async () => {
                        await handleAddPayment();
                        setShowAddPayment(false);
                        if (onUpdate) onUpdate();
                      }}
                      disabled={newPayment.amount <= 0}
                      sx={{ minWidth: 80 }}
                    >
                      Add
                    </Button>
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => setShowAddPayment(false)}
                      sx={{ minWidth: 80 }}
                    >
                      Cancel
                    </Button>
                  </Paper>
                )}
              </Box>
            )}
          </SectionCard>

          {/* Notes */}
          <SectionCard
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notes
                </Typography>
                {!showAddNote && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowAddNote(true)}
                    size="small"
                    sx={{ ml: 2, mt: { xs: 0, sm: 0 } }}
                  >
                    Add Note
                  </Button>
                )}
              </Box>
            }
            icon={<NoteIcon />}
          >
            {/* Existing notes list */}
            {sortedNotes.length > 0 ? (
              <>
                <List sx={{ p: 0 }}>
                  {(showAllNotes ? sortedNotes : sortedNotes.slice(0, 3)).map((note, idx) => (
                    <ListItem 
                      key={note._id || idx}
                      sx={{ 
                        px: 0, 
                        py: 1.5,
                        borderBottom: idx < (showAllNotes ? sortedNotes.length : Math.min(3, sortedNotes.length)) - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {note.content}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {note.date ? new Date(note.date).toLocaleDateString() : ''} • {note.author?.name || 'Unknown'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {sortedNotes.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => setShowAllNotes(v => !v)}
                    sx={{ mt: 1 }}
                  >
                    {showAllNotes ? 'Show Less' : 'Show More'}
                  </Button>
                )}
              </>
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No notes recorded.
              </Typography>
            )}
            {/* Add Note Form (unchanged) */}
            {showAddNote && (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  mb: 2,
                  maxWidth: 600,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  bgcolor: 'primary.50'
                }}
              >
                <TextField
                  fullWidth
                  label="Add Note"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mb: 1 }}
                  autoFocus
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      await handleAddNote();
                      setShowAddNote(false);
                      setNewNote('');
                      if (onUpdate) onUpdate();
                    }}
                    disabled={!newNote.trim()}
                  >
                    Add Note
                  </Button>
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => {
                      setShowAddNote(false);
                      setNewNote('');
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            )}
          </SectionCard>

          {/* Activity Log */}
          <SectionCard title="Activity Log" icon={<HistoryIcon />}>
            {sortedActivity.length > 0 ? (
              <>
                <List sx={{ p: 0 }}>
                  {(showAllActivity ? sortedActivity : sortedActivity.slice(0, 3)).map((log, idx) => (
                    <ListItem 
                      key={log._id || idx}
                      sx={{ 
                        px: 0, 
                        py: 1,
                        borderBottom: idx < (showAllActivity ? sortedActivity.length : Math.min(3, sortedActivity.length)) - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {log.action ? log.action.charAt(0).toUpperCase() + log.action.slice(1) : 'Unknown Action'}: {log.details}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {log.date ? 
                              `${new Date(log.date).toLocaleDateString('en-US')} ${new Date(log.date).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute:'2-digit' })}` 
                              : ''} • {log.user?.name || 'System'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {sortedActivity.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => setShowAllActivity(v => !v)}
                    sx={{ mt: 1 }}
                  >
                    {showAllActivity ? 'Show Less' : 'Show More'}
                  </Button>
                )}
              </>
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No activity logs available.
              </Typography>
            )}
          </SectionCard>
        </Box>
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          p: 3, 
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 2
        }}
      >
        {isEditing ? (
          <>
            <Button 
              onClick={handleCancelEdit} 
              variant="outlined" 
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary" 
              startIcon={<SaveIcon />}
              sx={{ borderRadius: 2 }}
            >
              Save Changes
            </Button>
          </>
        ) : (
          <Button 
            onClick={onClose} 
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        )}
      </DialogActions>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {isMobile && !isEditing && (
        <Fab
          color="primary"
          aria-label="edit"
          onClick={handleEditClick}
          sx={{ 
            position: 'fixed', 
            bottom: 32, 
            right: 32, 
            zIndex: 1301,
            boxShadow: 4
          }}
        >
          <EditIcon />
        </Fab>
      )}
    </Dialog>
  );
};

export default ReligiousEducationDetails; 